import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Bell, Send, Settings as SettingsIcon, Users, History, Loader2, Smartphone,
  Monitor, Tablet, ChevronLeft, ChevronRight, Save, Copy, Check, Wand2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function _relative(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diff = Date.now() - t;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)} h ago`;
  return new Date(iso).toLocaleDateString();
}

function _DeviceIcon({ deviceType, className }) {
  if (deviceType === 'mobile') return <Smartphone className={className} />;
  if (deviceType === 'tablet') return <Tablet className={className} />;
  return <Monitor className={className} />;
}

function authHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

/* =========================================================================
 * Setup tab — copy-paste snippet + company id
 * ======================================================================= */
function SetupTab({ user }) {
  const [copied, setCopied] = useState(false);
  const companyId = user?.company_id || '';
  const sdkUrl = `${API}/push/sdk.js?company_id=${companyId}`;
  const snippet = `<script src="${sdkUrl}" async defer></script>`;

  const copy = () => {
    navigator.clipboard?.writeText(snippet);
    setCopied(true);
    toast.success('Snippet copied');
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-4" data-testid="push-setup-tab">
      <Card className="bg-wt-paper border-wt-border p-5">
        <h3 className="text-wt-text-primary font-semibold mb-1">Embed snippet</h3>
        <p className="text-sm text-wt-text-muted mb-3">
          Paste this in your store's <code className="bg-wt-bg px-1 rounded">&lt;head&gt;</code>.
          On Shopify, enable the Opswarex Push Notifications app block instead — it does this automatically.
        </p>
        <pre className="bg-wt-bg border border-wt-border rounded-md px-3 py-2 text-xs text-wt-text-secondary overflow-x-auto" data-testid="push-embed-snippet">
{snippet}
        </pre>
        <Button
          variant="outline"
          size="sm"
          onClick={copy}
          className="mt-3"
          data-testid="push-copy-snippet"
        >
          {copied ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
          {copied ? 'Copied' : 'Copy snippet'}
        </Button>
      </Card>

      <Card className="bg-wt-paper border-wt-border p-5">
        <h3 className="text-wt-text-primary font-semibold mb-1">Company ID</h3>
        <p className="text-sm text-wt-text-muted mb-2">
          Paste this into the Shopify app block's "Opswarex Company ID" field.
        </p>
        <code className="bg-wt-bg px-3 py-2 rounded inline-block text-amber-400 text-sm font-mono" data-testid="push-company-id">
          {companyId || '(loading)'}
        </code>
      </Card>
    </div>
  );
}

/* =========================================================================
 * Settings tab
 * ======================================================================= */
function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/push/settings`, { headers: authHeaders() })
      .then((r) => setSettings(r.data))
      .catch(() => toast.error('Failed to load push settings'));
  }, []);

  const update = (path, value) => {
    setSettings((s) => {
      const next = { ...s };
      const segs = path.split('.');
      let cur = next;
      for (let i = 0; i < segs.length - 1; i++) {
        cur[segs[i]] = { ...(cur[segs[i]] || {}) };
        cur = cur[segs[i]];
      }
      cur[segs[segs.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: settings.enabled,
        prompt_style: settings.prompt_style,
        prompt: settings.prompt,
        trigger: settings.trigger,
        frequency: settings.frequency,
        store_name: settings.store_name,
      };
      await axios.put(`${API}/push/settings`, payload, { headers: authHeaders() });
      toast.success('Settings saved. Live within 5 minutes.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="flex items-center justify-center py-8 text-wt-text-muted">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="push-settings-tab">
      <Card className="bg-wt-paper border-wt-border p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-wt-text-primary font-semibold">Push notifications</p>
            <p className="text-xs text-wt-text-muted">Master switch for the SDK on your storefront.</p>
          </div>
          <Switch
            checked={!!settings.enabled}
            onCheckedChange={(v) => update('enabled', v)}
            data-testid="push-settings-enabled-toggle"
          />
        </div>
      </Card>

      <Card className="bg-wt-paper border-wt-border p-5 space-y-4">
        <div>
          <h3 className="text-wt-text-primary font-semibold">Prompt copy</h3>
          <p className="text-xs text-wt-text-muted">Use <code>{'{store_name}'}</code> for dynamic store name.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-wt-text-secondary text-xs">Title</Label>
            <Input
              value={settings.prompt?.title || ''}
              onChange={(e) => update('prompt.title', e.target.value)}
              className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              data-testid="push-prompt-title"
            />
          </div>
          <div>
            <Label className="text-wt-text-secondary text-xs">Store name (optional)</Label>
            <Input
              value={settings.store_name || ''}
              onChange={(e) => update('store_name', e.target.value)}
              placeholder="Your storefront name"
              className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              data-testid="push-store-name"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-wt-text-secondary text-xs">Body</Label>
            <Textarea
              rows={2}
              value={settings.prompt?.body || ''}
              onChange={(e) => update('prompt.body', e.target.value)}
              className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              data-testid="push-prompt-body"
            />
          </div>
          <div>
            <Label className="text-wt-text-secondary text-xs">Allow button label</Label>
            <Input
              value={settings.prompt?.allow_label || ''}
              onChange={(e) => update('prompt.allow_label', e.target.value)}
              className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              data-testid="push-prompt-allow"
            />
          </div>
          <div>
            <Label className="text-wt-text-secondary text-xs">Deny button label</Label>
            <Input
              value={settings.prompt?.deny_label || ''}
              onChange={(e) => update('prompt.deny_label', e.target.value)}
              className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              data-testid="push-prompt-deny"
            />
          </div>
        </div>
      </Card>

      <Card className="bg-wt-paper border-wt-border p-5 space-y-4">
        <div>
          <h3 className="text-wt-text-primary font-semibold">Trigger</h3>
          <p className="text-xs text-wt-text-muted">
            Fire the prompt when ANY of these match. Leave a field at 0 to disable that rule.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="text-wt-text-secondary text-xs">Time delay (s)</Label>
            <Input
              type="number" min="0"
              value={settings.trigger?.time_seconds ?? 0}
              onChange={(e) => update('trigger.time_seconds', parseInt(e.target.value) || 0)}
              className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              data-testid="push-trigger-time"
            />
          </div>
          <div>
            <Label className="text-wt-text-secondary text-xs">Scroll %</Label>
            <Input
              type="number" min="0" max="100"
              value={settings.trigger?.scroll_percent ?? 0}
              onChange={(e) => update('trigger.scroll_percent', parseInt(e.target.value) || 0)}
              className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              data-testid="push-trigger-scroll"
            />
          </div>
          <div>
            <Label className="text-wt-text-secondary text-xs">Page views</Label>
            <Input
              type="number" min="0"
              value={settings.trigger?.page_views ?? 0}
              onChange={(e) => update('trigger.page_views', parseInt(e.target.value) || 0)}
              className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              data-testid="push-trigger-pageviews"
            />
          </div>
        </div>
      </Card>

      <Card className="bg-wt-paper border-wt-border p-5 space-y-3">
        <h3 className="text-wt-text-primary font-semibold">Frequency cap</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { v: 'once_per_session', l: 'Once per session' },
            { v: 'once_per_day', l: 'Once per day' },
            { v: 'once_ever', l: 'Once ever' },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => update('frequency', opt.v)}
              className={`px-3 py-1.5 rounded-full text-xs border transition ${
                settings.frequency === opt.v
                  ? 'bg-amber-500 text-black border-amber-500'
                  : 'bg-wt-bg text-wt-text-secondary border-wt-border hover:border-amber-500/40'
              }`}
              data-testid={`push-frequency-${opt.v}`}
            >
              {opt.l}
            </button>
          ))}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
          data-testid="push-save-settings"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save settings
        </Button>
      </div>
    </div>
  );
}

/* =========================================================================
 * Subscribers tab
 * ======================================================================= */
function SubscribersTab() {
  const [data, setData] = useState({ subscribers: [], counts: { active: 0, identified: 0, expired: 0, unsubscribed: 0 }, pagination: { page: 1, pages: 1, total: 0 } });
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(false);

  const load = async (p = page, s = status) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/push/subscribers`, {
        headers: authHeaders(),
        params: { page: p, page_size: 25, status: s },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load subscribers');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(page, status); /* eslint-disable-next-line */ }, [page, status]);

  return (
    <div className="space-y-4" data-testid="push-subscribers-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { k: 'active', label: 'Active', tone: 'text-emerald-400', test: 'sub-count-active' },
          { k: 'identified', label: 'Identified', tone: 'text-amber-400', test: 'sub-count-identified' },
          { k: 'expired', label: 'Expired', tone: 'text-orange-400', test: 'sub-count-expired' },
          { k: 'unsubscribed', label: 'Unsubscribed', tone: 'text-gray-400', test: 'sub-count-unsubscribed' },
        ].map((c) => (
          <Card key={c.k} className="bg-wt-paper border-wt-border p-3" data-testid={c.test}>
            <p className="text-xs text-wt-text-muted">{c.label}</p>
            <p className={`text-xl font-bold ${c.tone}`}>{data.counts[c.k] ?? 0}</p>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {['active', 'identified', 'expired', 'unsubscribed', 'all'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => { setPage(1); setStatus(s); }}
            className={`px-3 py-1 rounded-full text-xs border transition capitalize ${
              status === s ? 'bg-amber-500 text-black border-amber-500'
                : 'bg-wt-bg text-wt-text-secondary border-wt-border hover:border-amber-500/40'
            }`}
            data-testid={`subscriber-filter-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      <Card className="bg-wt-paper border-wt-border overflow-hidden">
        <div className="divide-y divide-wt-border">
          {loading && (
            <div className="px-4 py-6 text-wt-text-muted flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && data.subscribers.length === 0 && (
            <div className="px-4 py-6 text-center text-wt-text-muted text-sm">
              No subscribers yet for this filter.
            </div>
          )}
          {data.subscribers.map((s, i) => (
            <div key={s.id || i} className="px-4 py-3 flex items-center gap-3" data-testid={`subscriber-row-${i}`}>
              <_DeviceIcon deviceType={s.device_type} className="h-4 w-4 text-amber-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-wt-text-primary capitalize truncate">
                  {(s.browser || 'browser') + ' · ' + (s.device_type || 'desktop')}
                  {s.customer_id && (
                    <Badge variant="outline" className="ml-2 border-amber-500/40 text-amber-300 text-[10px]">identified</Badge>
                  )}
                </p>
                <p className="text-xs text-wt-text-muted truncate">
                  {s.customer_email || s.customer_phone || 'Anonymous'} · subscribed {_relative(s.created_at)} · seen {_relative(s.last_seen_at)}
                </p>
              </div>
              <span className="text-[10px] uppercase tracking-wide text-wt-text-muted flex-shrink-0">
                {s.status}
              </span>
            </div>
          ))}
        </div>
        {data.pagination?.pages > 1 && (
          <div className="border-t border-wt-border px-3 py-2 flex items-center justify-between">
            <p className="text-xs text-wt-text-muted">
              Page {data.pagination.page} / {data.pagination.pages} · {data.pagination.total}
            </p>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="subscribers-prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" disabled={page >= data.pagination.pages} onClick={() => setPage(page + 1)} data-testid="subscribers-next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

/* =========================================================================
 * Compose tab — broadcast composer
 * ======================================================================= */
function ComposeTab() {
  const [form, setForm] = useState({
    title: '',
    body: '',
    image_url: '',
    cta_url: '',
    cta_label: 'View',
    campaign_name: '',
    customer_segment: 'all',
  });
  const [reach, setReach] = useState(null);
  const [sending, setSending] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadReach = async () => {
    try {
      const res = await axios.get(`${API}/push/audience-reach`, {
        headers: authHeaders(),
        params: { customer_segment: form.customer_segment },
      });
      setReach(res.data);
    } catch (e) { setReach(null); }
  };
  useEffect(() => { loadReach(); /* eslint-disable-next-line */ }, [form.customer_segment]);

  const valid = form.title.trim() && form.body.trim();

  const send = async () => {
    if (!valid) { toast.error('Title and body required'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/push/send`, {
        title: form.title.trim(),
        body: form.body.trim(),
        image_url: form.image_url.trim() || null,
        cta_url: form.cta_url.trim() || null,
        cta_label: form.cta_label.trim() || null,
        campaign_name: form.campaign_name.trim() || null,
        audience: { customer_segment: form.customer_segment },
      }, { headers: authHeaders() });
      const stats = res.data?.stats || {};
      toast.success(`Sent to ${stats.ok || 0} of ${stats.total || 0} subscribers`);
      setForm((f) => ({ ...f, title: '', body: '' }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4" data-testid="push-compose-tab">
      <Card className="bg-wt-paper border-wt-border p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-wt-text-secondary text-xs">Title <span className="text-red-400">*</span></Label>
            <Input
              maxLength={80}
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              placeholder="Flash sale starts now ⚡"
              className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              data-testid="compose-title"
            />
            <p className="text-[10px] text-wt-text-muted text-right">{form.title.length}/80</p>
          </div>
          <div className="col-span-2">
            <Label className="text-wt-text-secondary text-xs">Body <span className="text-red-400">*</span></Label>
            <Textarea
              rows={3} maxLength={240}
              value={form.body}
              onChange={(e) => update('body', e.target.value)}
              placeholder="40% off everything for the next 2 hours."
              className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              data-testid="compose-body"
            />
            <p className="text-[10px] text-wt-text-muted text-right">{form.body.length}/240</p>
          </div>
          <div>
            <Label className="text-wt-text-secondary text-xs">Image URL (optional)</Label>
            <Input value={form.image_url} onChange={(e) => update('image_url', e.target.value)} placeholder="https://cdn.shop.com/banner.jpg" className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="compose-image-url" />
          </div>
          <div>
            <Label className="text-wt-text-secondary text-xs">Campaign name (optional)</Label>
            <Input value={form.campaign_name} onChange={(e) => update('campaign_name', e.target.value)} placeholder="Spring Sale 26" className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="compose-campaign" />
          </div>
          <div>
            <Label className="text-wt-text-secondary text-xs">Click URL</Label>
            <Input value={form.cta_url} onChange={(e) => update('cta_url', e.target.value)} placeholder="https://shop.com/sale" className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="compose-cta-url" />
          </div>
          <div>
            <Label className="text-wt-text-secondary text-xs">Button label</Label>
            <Input value={form.cta_label} onChange={(e) => update('cta_label', e.target.value)} className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="compose-cta-label" />
          </div>
        </div>

        <div>
          <Label className="text-wt-text-secondary text-xs">Audience</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {[
              { v: 'all', l: 'All subscribers' },
              { v: 'identified', l: 'Identified only' },
              { v: 'anonymous', l: 'Anonymous only' },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => update('customer_segment', o.v)}
                className={`px-3 py-1.5 rounded-full text-xs border transition ${
                  form.customer_segment === o.v
                    ? 'bg-amber-500 text-black border-amber-500'
                    : 'bg-wt-bg text-wt-text-secondary border-wt-border hover:border-amber-500/40'
                }`}
                data-testid={`compose-audience-${o.v}`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={send}
            disabled={!valid || sending}
            className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
            data-testid="compose-send-button"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Send to {reach?.total ?? 0} {reach?.total === 1 ? 'subscriber' : 'subscribers'}
          </Button>
        </div>
      </Card>

      {/* Preview + reach */}
      <div className="space-y-4">
        <Card className="bg-wt-paper border-wt-border p-4">
          <p className="text-xs text-wt-text-muted mb-2">Estimated reach</p>
          <p className="text-3xl font-bold text-amber-400" data-testid="compose-reach-total">{reach?.total ?? '—'}</p>
          <p className="text-xs text-wt-text-muted mt-1">
            <span className="text-emerald-400 font-medium">{reach?.identified ?? 0}</span> identified ·{' '}
            <span className="text-gray-300">{reach?.anonymous ?? 0}</span> anonymous
          </p>
        </Card>
        <Card className="bg-wt-paper border-wt-border p-4">
          <p className="text-xs text-wt-text-muted mb-2">Preview</p>
          <div className="bg-white rounded-md p-3 shadow-inner border border-gray-200">
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 rounded bg-amber-500 flex items-center justify-center flex-shrink-0">
                <Bell className="h-4 w-4 text-black" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 leading-snug">{form.title || 'Your title here'}</p>
                <p className="text-xs text-gray-700 leading-snug mt-0.5">{form.body || 'Your message here.'}</p>
                {form.image_url && (
                  <img src={form.image_url} alt="preview" onError={(e) => { e.target.style.display = 'none'; }} className="mt-2 rounded border border-gray-200 max-h-28 object-cover" />
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* =========================================================================
 * History tab
 * ======================================================================= */
function HistoryTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/push/sends`, { headers: authHeaders() })
      .then((r) => setItems(r.data?.sends || []))
      .catch(() => toast.error('Failed to load history'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card className="bg-wt-paper border-wt-border overflow-hidden" data-testid="push-history-tab">
      <div className="divide-y divide-wt-border">
        {loading && (
          <div className="px-4 py-6 text-wt-text-muted flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-4 py-8 text-center text-wt-text-muted text-sm">No broadcasts yet.</div>
        )}
        {items.map((s, i) => {
          const stats = s.stats || {};
          return (
            <div key={s.id || i} className="px-4 py-3 flex items-start gap-3" data-testid={`push-history-row-${i}`}>
              <Send className="h-4 w-4 text-amber-400 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-wt-text-primary truncate">{s.title}</p>
                <p className="text-xs text-wt-text-muted truncate">
                  {s.body}
                </p>
                <p className="text-[11px] text-wt-text-muted mt-0.5">
                  {_relative(s.sent_at)} · {s.campaign_name || (s.journey_id ? 'via journey' : 'broadcast')}
                </p>
              </div>
              <div className="text-right flex-shrink-0 text-xs">
                <p className="text-emerald-400 font-medium">{stats.ok || 0} sent</p>
                <p className="text-wt-text-muted">{stats.total || 0} targeted</p>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* =========================================================================
 * Page shell
 * ======================================================================= */
export default function PushNotificationsPage() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      setUser(u);
    } catch { /* noop */ }
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="px-4 sm:px-6 py-6 max-w-6xl mx-auto"
      data-testid="push-notifications-page"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
          <Bell className="h-5 w-5 text-amber-400" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-wt-text-primary">Push Notifications</h1>
          <p className="text-sm text-wt-text-muted">Capture web subscribers and reach them across journeys and broadcasts.</p>
        </div>
      </div>

      <Tabs defaultValue="compose" className="w-full">
        <TabsList className="bg-wt-bg border border-wt-border w-full justify-start mb-4 overflow-x-auto">
          <TabsTrigger value="compose" data-testid="push-tab-compose"><Wand2 className="h-4 w-4 mr-1.5" /> Compose</TabsTrigger>
          <TabsTrigger value="subscribers" data-testid="push-tab-subscribers"><Users className="h-4 w-4 mr-1.5" /> Subscribers</TabsTrigger>
          <TabsTrigger value="history" data-testid="push-tab-history"><History className="h-4 w-4 mr-1.5" /> History</TabsTrigger>
          <TabsTrigger value="settings" data-testid="push-tab-settings"><SettingsIcon className="h-4 w-4 mr-1.5" /> Settings</TabsTrigger>
          <TabsTrigger value="setup" data-testid="push-tab-setup"><Bell className="h-4 w-4 mr-1.5" /> Setup</TabsTrigger>
        </TabsList>
        <TabsContent value="compose"><ComposeTab /></TabsContent>
        <TabsContent value="subscribers"><SubscribersTab /></TabsContent>
        <TabsContent value="history"><HistoryTab /></TabsContent>
        <TabsContent value="settings"><SettingsTab /></TabsContent>
        <TabsContent value="setup"><SetupTab user={user} /></TabsContent>
      </Tabs>
    </motion.div>
  );
}
